import type {
  MaterialDto,
  ProductDto,
  ProductRevisionDto,
  QuoteDto,
  QuoteRevisionDto,
  RfqDto,
  SalesOrderDto,
  WorkCenterDto,
} from "@grizzly/shared";
import { BASE_URL, request } from "../http.js";

export function quotePdfUrl(id: string): string {
  return `${BASE_URL}/quotes/${id}/pdf`;
}

export function jobPdfUrl(id: string): string {
  return `${BASE_URL}/jobs/${id}/pdf`;
}

export const catalogApi = {
  listWorkCenters: () => request<{ items: WorkCenterDto[] }>("/work-centers"),
  createWorkCenter: (input: object) =>
    request<{ workCenter: WorkCenterDto }>("/work-centers", {
      method: "POST",
      body: input,
    }),
  listMaterials: () => request<{ items: MaterialDto[] }>("/materials"),
  createMaterial: (input: object) =>
    request<{ material: MaterialDto }>("/materials", { method: "POST", body: input }),
  listProducts: () => request<{ items: ProductDto[] }>("/products"),
  createProduct: (input: object) =>
    request<{ product: ProductDto }>("/products", { method: "POST", body: input }),
  createProductRevision: (productId: string, input: object) =>
    request<{ revision: ProductRevisionDto }>(`/products/${productId}/revision`, {
      method: "POST",
      body: input,
    }),
  getProductRevision: (id: string) =>
    request<{ revision: ProductRevisionDto }>(`/product-revisions/${id}`),
};

export const salesApi = {
  listRfqs: () => request<{ items: RfqDto[] }>("/rfqs"),
  createRfq: (input: object) =>
    request<{ rfq: RfqDto }>("/rfqs", { method: "POST", body: input }),
  listQuotes: (status?: string) =>
    request<{ items: QuoteDto[] }>(
      `/quotes${status ? `?status=${encodeURIComponent(status)}` : ""}`
    ),
  createQuote: (input: object) =>
    request<{ quote: QuoteDto; revision: QuoteRevisionDto }>("/quotes", {
      method: "POST",
      body: input,
    }),
  getQuote: (id: string) =>
    request<{ quote: QuoteDto; revision: QuoteRevisionDto | null }>(`/quotes/${id}`),
  sendQuote: (id: string) =>
    request<{ revision: QuoteRevisionDto }>(`/quotes/${id}/send`, {
      method: "POST",
      body: {},
    }),
  reviseQuote: (id: string) =>
    request<{ revision: QuoteRevisionDto }>(`/quotes/${id}/revise`, {
      method: "POST",
      body: {},
    }),
  acceptQuote: (id: string) =>
    request<{ order: SalesOrderDto }>(`/quotes/${id}/accept`, {
      method: "POST",
      body: {},
    }),
  declineQuote: (id: string, reason?: string) =>
    request<{ ok: boolean }>(`/quotes/${id}/decline`, {
      method: "POST",
      body: { reason },
    }),
  addQuoteLine: (quoteId: string, revisionId: string, input: object) =>
    request<{ revision: QuoteRevisionDto }>(
      `/quotes/${quoteId}/revisions/${revisionId}/lines`,
      {
        method: "POST",
        body: input,
      }
    ),
  updateQuoteLine: (quoteId: string, revisionId: string, lineId: string, input: object) =>
    request<{ revision: QuoteRevisionDto }>(
      `/quotes/${quoteId}/revisions/${revisionId}/lines/${lineId}/update`,
      { method: "POST", body: input }
    ),
  deleteQuoteLine: (quoteId: string, revisionId: string, lineId: string) =>
    request<{ revision: QuoteRevisionDto }>(
      `/quotes/${quoteId}/revisions/${revisionId}/lines/${lineId}/delete`,
      { method: "POST", body: {} }
    ),
  setEstimates: (
    quoteId: string,
    revisionId: string,
    lineId: string,
    estimates: unknown[]
  ) =>
    request<{ revision: QuoteRevisionDto }>(
      `/quotes/${quoteId}/revisions/${revisionId}/lines/${lineId}/estimates`,
      { method: "POST", body: { estimates } }
    ),
  listSalesOrders: () => request<{ items: SalesOrderDto[] }>("/sales-orders"),
  getSalesOrder: (id: string) => request<{ order: SalesOrderDto }>(`/sales-orders/${id}`),
};
